// Single catalog of the spawnable power-ups, matching core's powerUpDefinitions
// keys. Drives the local New Match settings panel (and could back the Help list).
// `key` is the core powerUpDefinitions key; `icon` is the svg basename under
// /svg/powerup/; `color` follows the affects convention (self=green, others=red,
// all=blue). See core/src/core/constants.js.
import { POWER_UP_SPAWN_CHANCE, WORM_SPEED } from "core/src/core/constants.js";

export type PowerUpColor = "green" | "red" | "blue";

export interface PowerUpMeta {
  key: string;
  name: string;
  icon: string;
  color: PowerUpColor;
}

export const POWERUPS: PowerUpMeta[] = [
  // Green — helps you (affects: self)
  { key: "slow", name: "Slow", icon: "slow", color: "green" },
  { key: "slim", name: "Slim", icon: "slim", color: "green" },
  { key: "quick_turn", name: "Quick turn", icon: "quickturn", color: "green" },
  { key: "super_jump", name: "Super jump", icon: "superjump", color: "green" },
  { key: "twin", name: "Twin", icon: "twin", color: "green" },
  // NOTE: "wall_hack_self" (green wall hack) is removed from the game for now (core weight 0).
  // Only the blue "all" wall hack spawns. Re-add here + restore its core weight to bring it back.
  // Red — hurts everyone else (affects: others)
  { key: "speed", name: "Speed", icon: "speed", color: "red" },
  { key: "fat", name: "Fat", icon: "fat", color: "red" },
  { key: "slow_turn", name: "Slow turn", icon: "slowturn", color: "red" },
  { key: "key_switch", name: "Reversed keys", icon: "switchkeys", color: "red" },
  { key: "drunk", name: "Drunk", icon: "drunk", color: "red" },
  { key: "tron_turn", name: "Tron turn", icon: "tronturn", color: "red" },
  // Blue — affects everyone (affects: all)
  { key: "switcharoonie", name: "Switcharoonie", icon: "switcharoonie", color: "blue" },
  // NOTE: "clear_all" (Clear) is removed from the game for now (core weight 0). Re-add here +
  // restore its core weight to bring it back.
  { key: "wall_hack_all", name: "Wall hack", icon: "wallhack", color: "blue" },
];

export const ALL_POWERUP_KEYS: string[] = POWERUPS.map((p) => p.key);

// In-game pickup circle colors (match power-up-renderer's POWERUP_COLORS).
export const POWERUP_BADGE: Record<PowerUpColor, string> = {
  green: "#008000",
  red: "#FF0B0B",
  blue: "#3453BC",
};

// Spawn-rate presets. Normal = the engine default; the others fan out around it.
// value is the per-tick-ish chance passed to the engine (0 = no power-ups).
export const DEFAULT_SPAWN_CHANCE: number = POWER_UP_SPAWN_CHANCE;
export const SPAWN_RATES: { label: string; value: number }[] = [
  { label: "Off", value: 0 },
  { label: "Rare", value: +(POWER_UP_SPAWN_CHANCE * 0.4).toFixed(3) },
  { label: "Normal", value: POWER_UP_SPAWN_CHANCE },
  { label: "Frequent", value: +(POWER_UP_SPAWN_CHANCE * 2).toFixed(3) },
  { label: "Chaos", value: +(POWER_UP_SPAWN_CHANCE * 5).toFixed(3) },
];

// Worm-speed presets. Normal = the engine default.
export const DEFAULT_WORM_SPEED: number = WORM_SPEED;
export const WORM_SPEEDS: { label: string; value: number }[] = [
  { label: "Slow", value: Math.round(WORM_SPEED * 0.7) },
  { label: "Normal", value: WORM_SPEED },
  { label: "Fast", value: Math.round(WORM_SPEED * 1.4) },
];
